import { useState } from "react";
import {
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  InputField,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Switch,
  Alert,
  AlertTitle,
} from "@jem-hr/jem-ui";
import { History, Plus, Users, AlertCircle } from "lucide-react";
import { BranchFilter } from "../components/BranchFilter";

// ─── Types ───────────────────────────────────────────────────────────────────

interface SegmentationBlock {
  id: string;
  name: string;
  reason: string;
  segment: string;
  benefits: string[];
  employees: number;
  createdAt: string;
  createdBy: string;
  active: boolean;
}

// ─── Data ────────────────────────────────────────────────────────────────────

const segmentationBlocks: SegmentationBlock[] = [
  { id: "1", name: "Employees <R5000", reason: "You are not eligible for EWA", segment: "Salary <R5000", benefits: ["Advances", "Jem Mobile"], employees: 44, createdAt: "2026-02-05 10:22", createdBy: "Alexandra Bohmer", active: true },
  { id: "2", name: "Employees with R0", reason: "R0 salary employees are not eligible", segment: "R0 employees", benefits: ["Advances"], employees: 28, createdAt: "2026-01-27 16:15", createdBy: "Alexandra Bohmer", active: true },
  { id: "3", name: "Test pay cycle block", reason: "Testing pay cycle blocking", segment: "Test pay cycle block", benefits: ["VAS"], employees: 1, createdAt: "2026-02-03 11:24", createdBy: "Rodrigue Tusse", active: false },
  { id: "4", name: "R0 salary block test", reason: "R0 salary block test reason", segment: "R0 Salary", benefits: ["Advances", "VAS"], employees: 28, createdAt: "2026-01-26 15:05", createdBy: "Alexandra Bohmer", active: false },
  { id: "5", name: "Rodrigue", reason: "Block rodrigue", segment: "Rodrigue", benefits: ["Jem Mobile"], employees: 1, createdAt: "2026-01-23 17:51", createdBy: "Rodrigue Tusse", active: false },
];

const activeBlocks = segmentationBlocks.filter((b) => b.active);
const inactiveBlocks = segmentationBlocks.filter((b) => !b.active);

// ─── Block row component ─────────────────────────────────────────────────────

function BlockRow({
  block,
  isLast,
  onEdit,
}: {
  block: SegmentationBlock;
  isLast: boolean;
  onEdit: () => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between py-md">
        <div className="flex items-center gap-sm">
          {/* Colour dot */}
          <div
            className={`h-[40px] w-[40px] rounded-full shrink-0 ${
              block.active ? "bg-secondary-pink-300" : "bg-neutral-300"
            }`}
          />

          <div>
            <div className="flex items-center gap-xxs">
              <span className="text-sm font-semibold text-greyscale-text-title">
                {block.name}
              </span>
              <span className="inline-flex items-center gap-xxxs rounded-full border border-greyscale-border px-xs py-[2px] text-xs text-greyscale-text-body">
                <Users size={12} />
                {block.employees} employees
              </span>
            </div>
            <p className="text-xs text-greyscale-text-caption mt-xxxs">
              Created: {block.createdAt} by {block.createdBy}
            </p>
          </div>
        </div>

        <Button variant="outline" size="medium" onClick={onEdit}>
          Edit
        </Button>
      </div>
      {!isLast && <div className="border-t border-greyscale-border" />}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FinancialBenefits() {
  const [activeTab, setActiveTab] = useState("segmentation-blocks");
  const [showForm, setShowForm] = useState(false);
  const [editingBlock, setEditingBlock] = useState<SegmentationBlock | null>(
    null
  );

  const isFormVisible = showForm || editingBlock !== null;

  return (
    <div>
      {/* Header */}
      <div
        className="flex items-center justify-between -mx-xl -mt-lg px-xl py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <div>
          <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
            Financial Benefits
          </h1>
          <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
            Configure limits, blocked dates, and segmentation blocks for employee financial benefits.
          </p>
        </div>
        {!isFormVisible && (
          <div className="flex items-center gap-xxs">
            <Button leftIcon={<History size={16} />}>
              View History Updates
            </Button>
            <Button
              leftIcon={<Plus size={16} />}
              onClick={() => setShowForm(true)}
            >
              Create New
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList variant="line" className="w-full justify-start">
          <TabsTrigger value="limits" variant="line">
            Limits
          </TabsTrigger>
          <TabsTrigger value="blocked-dates" variant="line">
            Blocked Dates
          </TabsTrigger>
          <TabsTrigger value="segmentation-blocks" variant="line">
            Segmentation Blocks
          </TabsTrigger>
        </TabsList>

        {/* ── Limits (placeholder) ── */}
        <TabsContent value="limits">
          <p className="text-sm text-greyscale-text-caption mt-md">
            Limits content coming soon.
          </p>
        </TabsContent>

        {/* ── Blocked Dates (placeholder) ── */}
        <TabsContent value="blocked-dates">
          <p className="text-sm text-greyscale-text-caption mt-md">
            Blocked Dates content coming soon.
          </p>
        </TabsContent>

        {/* ── Segmentation Blocks ── */}
        <TabsContent value="segmentation-blocks">
          {/* ── Create form ── */}
          {showForm && (
            <div className="mt-md">
              <div className="max-w-[850px]">
                <p className="text-base font-semibold text-greyscale-text-title mb-md">
                  New Segmentation Block
                </p>

                <div className="flex flex-col gap-md">
                  <InputField
                    label="Title"
                    required
                    placeholder="Enter title"
                  />

                  <div className="flex flex-col gap-xxxs">
                    <div>
                      <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                        Reason for block{" "}
                        <span className="text-red-500">*</span>
                      </p>
                      <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                        *Note: This message will be visible to the employee.
                      </p>
                    </div>
                    <Textarea
                      placeholder="Enter reason for block"
                      className="min-h-[115px]"
                    />
                  </div>

                  <div className="flex flex-col gap-xxxs">
                    <div>
                      <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                        Apply to financial benefits
                      </p>
                      <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                        Choose which financial benefits this block applies to
                      </p>
                    </div>
                    <BranchFilter
                      branches={["Jem Mobile", "Advances", "VAS"]}
                      selectedBranches={[]}
                      label=""
                      placeholder="Select benefits..."
                      searchPlaceholder="Search benefits..."
                      className="max-w-full"
                    />
                  </div>

                  <div className="flex flex-col gap-xxxs">
                    <div>
                      <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                        Select employee segments
                      </p>
                      <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                        Choose which employee segments to block from EWA
                        advances
                      </p>
                    </div>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select segments" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employees-r5000">
                          Employees &lt;R5000
                        </SelectItem>
                        <SelectItem value="employees-r0">
                          Employees with R0
                        </SelectItem>
                        <SelectItem value="test-pay-cycle">
                          Test pay cycle block
                        </SelectItem>
                        <SelectItem value="r0-salary">
                          R0 salary block test
                        </SelectItem>
                        <SelectItem value="rodrigue">Rodrigue</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                        Segment status
                      </p>
                      <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                        Activate segment by switching the toggle. Default is
                        deactivated.
                      </p>
                    </div>
                    <Switch />
                  </div>
                </div>
              </div>

              <div className="border-t border-greyscale-border mt-lg" />
              <div className="flex items-center justify-between py-lg">
                <Button variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button>Save Segmentation Block</Button>
              </div>
            </div>
          )}

          {/* ── Edit view ── */}
          {editingBlock && (
            <div className="mt-md">
              <div className="max-w-[850px]">
                <p className="text-base font-semibold text-greyscale-text-title mb-md">
                  Edit Segmentation Block
                </p>

                {/* Block details card */}
                <div className="rounded-lg border border-greyscale-border bg-neutral-50 p-md flex flex-col gap-sm">
                  <div>
                    <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                      Title
                    </p>
                    <p className="text-sm text-greyscale-text-caption mt-xxxs">
                      {editingBlock.name}
                    </p>
                  </div>

                  <div className="border-t border-greyscale-border" />

                  <div>
                    <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                      Reason for block
                    </p>
                    <p className="text-sm text-greyscale-text-caption mt-xxxs">
                      {editingBlock.reason}
                    </p>
                  </div>

                  <div className="border-t border-greyscale-border" />

                  <div>
                    <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                      Applied financial benefits
                    </p>
                    <p className="text-sm text-greyscale-text-caption mt-xxxs">
                      {editingBlock.benefits.join(", ")}
                    </p>
                  </div>

                  <div className="border-t border-greyscale-border" />

                  <div>
                    <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                      Applied employee segments
                    </p>
                    <p className="text-sm text-greyscale-text-caption mt-xxxs">
                      {editingBlock.segment} ({editingBlock.employees})
                    </p>
                  </div>

                  <div className="border-t border-greyscale-border" />

                  <div>
                    <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                      Date created
                    </p>
                    <p className="text-sm text-greyscale-text-caption mt-xxxs">
                      {editingBlock.createdAt}
                    </p>
                  </div>

                  <div className="border-t border-greyscale-border" />

                  <div>
                    <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                      Created by (User)
                    </p>
                    <p className="text-sm text-greyscale-text-caption mt-xxxs">
                      {editingBlock.createdBy}
                    </p>
                  </div>
                </div>

                {/* Estimated match alert */}
                <div className="mt-md">
                  <Alert variant="note">
                    <AlertTitle icon={<AlertCircle size={16} />}>
                      Estimated match: {editingBlock.employees} Employees
                    </AlertTitle>
                  </Alert>
                </div>

                {/* Segment status toggle */}
                <div className="flex items-center justify-between mt-md">
                  <div>
                    <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                      Segment status
                    </p>
                    <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                      Activate segment by switching the toggle. Default is
                      deactivated.
                    </p>
                  </div>
                  <Switch defaultChecked={editingBlock.active} />
                </div>
              </div>

              <div className="border-t border-greyscale-border mt-lg" />
              <div className="flex items-center justify-between py-lg">
                <Button
                  variant="outline"
                  onClick={() => setEditingBlock(null)}
                >
                  Cancel
                </Button>
                <Button>Save Segmentation Block</Button>
              </div>
            </div>
          )}

          {/* ── Block list ── */}
          {!showForm && !editingBlock && (
            <div className="mt-md">
              <h2 className="text-base font-semibold text-greyscale-text-title mb-xxs">
                Active segmentation blocks
              </h2>
              <div>
                {activeBlocks.map((block, i) => (
                  <BlockRow
                    key={block.id}
                    block={block}
                    isLast={i === activeBlocks.length - 1}
                    onEdit={() => setEditingBlock(block)}
                  />
                ))}
              </div>

              <h2 className="text-base font-semibold text-greyscale-text-title mt-lg mb-xxs">
                Inactive segmentation blocks
              </h2>
              <div>
                {inactiveBlocks.map((block, i) => (
                  <BlockRow
                    key={block.id}
                    block={block}
                    isLast={i === inactiveBlocks.length - 1}
                    onEdit={() => setEditingBlock(block)}
                  />
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
